import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { getPublicRoutePolicy } from './public-route-policy';

@Injectable({ providedIn: 'root' })
export class CanonicalUrlService {
  private readonly publicOrigin = new URL(environment.config.APP_URL).origin;
  private readonly trackingParameter = /^(?:utm_[a-z0-9_]+|gclid|dclid|gbraid|wbraid|fbclid|msclkid|yclid|_ga|_gl)$/i;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly meta: Meta,
  ) {}

  setFromUrl(url: string): void {
    const normalizedUrl = this.normaliseUrl(url);
    const policy = getPublicRoutePolicy(normalizedUrl.path);
    const marketPairAwaitingResolution = /^\/markets\/[A-Z0-9]+\/[A-Z0-9]+$/.test(normalizedUrl.path);

    // Coin aliases need public coin data before they can receive a canonical URL.
    if (/^\/coins\/[^/]+$/i.test(normalizedUrl.path)) {
      this.clearCanonical();
    } else if (policy.canonicalPath) {
      this.setPath(policy.canonicalPath, normalizedUrl.query);
    } else {
      this.clearCanonical();
    }

    // The prerendered market route is already known to be supported. Keep its
    // crawlable state while client market data resolves; MarketsComponent will
    // mark unknown pairs noindex and confirm the canonical for supported pairs.
    if (marketPairAwaitingResolution && environment.config.INDEXING_ENABLED) {
      this.meta.removeTag('name="robots"');
    } else {
      this.applyIndexingPolicy(policy.indexable);
    }
  }

  markNotIndexable(): void {
    this.applyIndexingPolicy(false);
  }

  setCoinCode(code: string): void {
    const canonicalCode = code.trim().toUpperCase();
    if (!canonicalCode) {
      this.clearCanonical();
      return;
    }

    this.setPath(`/coins/${encodeURIComponent(canonicalCode)}`);
    this.applyIndexingPolicy(canonicalCode !== 'CZAR');
  }

  setMarketPair(coinCode: string, exchangeCode: string): void {
    const coin = coinCode.trim().toUpperCase();
    const exchange = exchangeCode.trim().toUpperCase();
    if (!/^[A-Z0-9]+$/.test(coin) || !/^[A-Z0-9]+$/.test(exchange)) {
      this.clearCanonical();
      this.applyIndexingPolicy(false);
      return;
    }

    this.setPath(`/markets/${encodeURIComponent(coin)}/${encodeURIComponent(exchange)}`);
    this.applyIndexingPolicy(true);
  }

  clearCanonical(): void {
    this.document.querySelectorAll('link[rel="canonical"]').forEach(canonical => canonical.remove());
    this.meta.removeTag('property="og:url"');
  }

  private setPath(path: string, query: string = ''): void {
    const canonicalUrl = new URL(this.publicOrigin);
    canonicalUrl.pathname = path;
    canonicalUrl.search = query;

    const canonicals = this.document.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]');
    let canonical = canonicals.item(0);
    canonicals.forEach((duplicate, index) => {
      if (index > 0) {
        duplicate.remove();
      }
    });
    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.rel = 'canonical';
      this.document.head.appendChild(canonical);
    }

    canonical.href = canonicalUrl.href;
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl.href });
  }

  private normaliseUrl(url: string): { path: string; query: string } {
    const routeUrl = new URL(url || '/', this.publicOrigin);
    const path = routeUrl.pathname.replace(/\/+$/, '').replace(/\/index\.html$/i, '') || '/';
    const query = new URLSearchParams();
    routeUrl.searchParams.forEach((value, name) => {
      if (!this.trackingParameter.test(name)) {
        query.append(name, value);
      }
    });
    query.sort();
    return { path, query: query.toString() };
  }

  private applyIndexingPolicy(indexable: boolean): void {
    if (!environment.config.INDEXING_ENABLED || !indexable) {
      this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    } else {
      this.meta.removeTag('name="robots"');
    }
  }
}
